import { useState } from "react";
import { useAuth } from "#/context/AuthProvider";
import { MEMBERSHIP_PERMISSIONS } from "../permissions";
import type { MemberFeeRecord } from "../types";
import { MembershipStatusBadge } from "./MembershipStatusBadge";

const PAGE_SIZE = 5;

function fmtDate(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return v; }
}

function fmtAmt(v?: number | null, currency?: string | null) {
  if (v === null || v === undefined) return <span className="text-xs italic text-slate-400">Restricted</span>;
  return <span>{currency ?? "KES"} {v.toLocaleString("en-KE", { minimumFractionDigits: 2 })}</span>;
}

export function MembershipFinancePanel({
  fees,
  onReload: _onReload,
}: {
  fees: MemberFeeRecord[];
  onReload: () => void;
}) {
  const [page, setPage] = useState(0);
  const { hasAnyPermission } = useAuth();
  const canViewAmounts = hasAnyPermission([
    MEMBERSHIP_PERMISSIONS.FINANCE_VIEW,
    MEMBERSHIP_PERMISSIONS.FINANCE_UPDATE,
  ]);

  const totalPages = Math.ceil(fees.length / PAGE_SIZE) || 1;
  const visible = fees.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-black text-slate-900">Finance Records</h2>
          <p className="mt-0.5 text-sm text-slate-500">{fees.length} fee record{fees.length !== 1 ? "s" : ""}</p>
        </div>
        {!canViewAmounts && (
          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">
            Amounts restricted
          </span>
        )}
      </div>

      {fees.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">
          No fee records found.
        </div>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Fee Type</th>
                  <th className="px-4 py-3">Year</th>
                  <th className="px-4 py-3">Due Date</th>
                  <th className="px-4 py-3">Status</th>
                  {canViewAmounts && (
                    <>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Paid</th>
                    </>
                  )}
                  <th className="px-4 py-3">Payment Ref</th>
                  <th className="px-4 py-3">Payment Date</th>
                  <th className="px-4 py-3">Confirmed By</th>
                  <th className="px-4 py-3">Confirmed At</th>
                  <th className="px-4 py-3">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((f) => (
                  <tr key={f.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-bold text-slate-900 whitespace-nowrap">{f.feeTypeName}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{f.financialYear}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(f.dueDate)}</td>
                    <td className="px-4 py-3"><MembershipStatusBadge value={f.paymentStatus} /></td>
                    {canViewAmounts && (
                      <>
                        <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(f.amount, f.currency)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{fmtAmt(f.amountPaid, f.currency)}</td>
                      </>
                    )}
                    <td className="px-4 py-3 text-xs text-slate-600">{f.paymentReference ?? "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{fmtDate(f.paymentDate)}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{f.confirmedBy ?? "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{fmtDate(f.confirmedAt)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{f.financeNotes ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-slate-400">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, fees.length)} of {fees.length}
              </span>
              <div className="flex gap-2">
                <button type="button" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold disabled:opacity-40">Prev</button>
                <button type="button" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
